export default defineNuxtPlugin(async (nuxtApp) => {
  const config = useRuntimeConfig()
  const { clientId, bucketName } = config.public
  const timeout = 60 * 1000

  // SSR-safe: 使用 nuxtApp context 存儲狀態和 timer，避免記憶體洩漏
  if (!nuxtApp._hostTesterContext) {
    nuxtApp._hostTesterContext = {
      fastApiUrl: null,
      intervalId: null,
      hostTester: null,
      isTestRunning: false,
    }
  }
  const ctx = nuxtApp._hostTesterContext

  // 如果已經有 interval 在運行，不重複建立
  if (ctx.intervalId) {
    return {
      provide: {
        hostTester: ctx.hostTester,
        getFastApiUrl: () => ctx.fastApiUrl,
      },
    }
  }

  const url = `https://storage.googleapis.com/${bucketName}/site_resources/${clientId}/target/target.json`
  ctx.hostTester = new HostTester(url, clientId)

  // 初始測試
  ctx.fastApiUrl = await ctx.hostTester.test()

  // 定期更新最佳 API URL（使用防重入機制避免併發測試）
  ctx.intervalId = setInterval(async () => {
    // 防止併發測試：如果上一次測試還沒完成，跳過這次
    if (ctx.isTestRunning) {
      return
    }
    ctx.isTestRunning = true
    try {
      ctx.fastApiUrl = await ctx.hostTester.test()
    } finally {
      ctx.isTestRunning = false
    }
  }, timeout)

  // 清理機制：頁面卸載時清除 interval
  if (typeof window !== 'undefined') {
    const cleanup = () => {
      if (ctx.intervalId) {
        clearInterval(ctx.intervalId)
        ctx.intervalId = null
      }
    }

    window.addEventListener('beforeunload', cleanup, { once: true })

    // Nuxt app 卸載時也清理
    nuxtApp.hook('app:unmount', cleanup)
  }

  return {
    provide: {
      hostTester: ctx.hostTester,
      getFastApiUrl: () => ctx.fastApiUrl,
    },
  }
})

class HostTester {
  constructor(url, clientId) {
    this.url = url
    this.hosts = []
    this.clientId = clientId
    this.lastFastUrl = null
    this.abortController = null
  }

  async init() {
    const target = await fetch(this.url)
    const { host } = await target.json()
    this.hosts = host
  }

  async test() {
    // 取消上一次未完成的測試（避免累積 pending promises）
    if (this.abortController) {
      this.abortController.abort()
    }
    this.abortController = new AbortController()
    const signal = this.abortController.signal

    try {
      if (this.hosts.length === 0) {
        await this.init()
      }
      const { url, index } = await Promise.any(
        this.hosts.map((url, i) => this.ping(url, i, signal))
      )
      console.info(`[Network] Host ${index} is chosen. (${url})`)
      this.lastFastUrl = url
      return url
    } catch (error) {
      // 如果是被中止的，不輸出錯誤
      if (error.name === 'AbortError') {
        return this.lastFastUrl
      }
      console.error('[Network] Host Test Fail.', error)
      // 返回上一次的結果，避免中斷服務
      return this.lastFastUrl
    } finally {
      this.abortController = null
    }
  }

  async ping(url, index, signal) {
    const ts = Date.now()
    let response
    try {
      response = await fetch(`${url}/health?ts=${ts}&site=${this.clientId}`, { signal })
      return { url, index }
    } finally {
      const speed = Date.now() - ts
      const status = response?.ok ? 'Success' : 'Failure'
      console.info(`[Network] Host: ${index}, URL: ${url}, Status: ${status}, Speed: ${speed}ms`)
    }
  }
}
